import { mapDisplayPartToPath, mapPathToDisplayParts } from '@src/containers/Routes/utils';
import { useBaseTitle } from '@src/hooks/useBaseTitle';
import { useNavigationItems } from '@src/hooks/useNavigationItems';
import { useTabLabelResolver } from '@src/hooks/useTabLabelResolver';
import { Breadcrumb, BreadcrumbItem } from '@talos/kyoko';
import { compact, startCase } from 'lodash-es';
import { useEffect, useMemo } from 'react';
import { matchRoutes, Route, Routes, useLocation } from 'react-router-dom';
import { useFeatureFlag } from '../../hooks';

const breadcrumbRoutes = [
  'trading',
  'dealer',
  'transfers',
  'portfolio',
  'portfolio-engineering',
  'analytics',
  'monitoring',
  'settings',
  'admin',
  'talos-admin',
] as const;

export const AppBreadcrumbs = () => {
  const { enableTradingSpaces } = useFeatureFlag();
  const baseTitle = useBaseTitle();
  const location = useLocation();

  const { resolveTabLabel } = useTabLabelResolver();
  const { navItems } = useNavigationItems();

  const { parts, routeMatches } = useMemo(() => {
    const routeMatches = matchRoutes(navItems, location.pathname);

    // Temporary fix to show Trading breadcrumb for Flexible Layout on /trading routes
    const parts =
      !enableTradingSpaces && location.pathname.startsWith('/trading')
        ? ['Trading']
        : compact(mapPathToDisplayParts(resolveTabLabel, location.pathname, routeMatches ?? []));

    return { parts, routeMatches };
  }, [location.pathname, navItems, resolveTabLabel, enableTradingSpaces]);

  const breadcrumbItems = useMemo(() => {
    return parts.map((part, index) => {
      const to = routeMatches?.at(index)
        ? routeMatches[index].pathname
        : `/${mapDisplayPartToPath(parts.slice(0, index + 1), location.pathname)}`;

      const capitalize = !part.startsWith('#');

      return (
        <BreadcrumbItem
          key={index}
          to={index < parts.length - 1 ? to : '#'}
          onClick={e => !to && e.preventDefault()}
          style={{ textTransform: capitalize ? 'capitalize' : 'none' }}
        >
          {part}
        </BreadcrumbItem>
      );
    });
  }, [parts, routeMatches, location.pathname]);

  useEffect(() => {
    const titleParts = parts.map(startCase);
    document.title = titleParts.length > 0 ? `${baseTitle} - ${titleParts.join(' > ')}` : baseTitle;
  }, [baseTitle, parts]);

  return (
    <Routes>
      {breadcrumbRoutes.map(path => (
        <Route key={path} path={`/${path}/*`} element={<Breadcrumb>{breadcrumbItems}</Breadcrumb>} />
      ))}
    </Routes>
  );
};
